import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", location: "", acreage: "", planning_status: "Unknown", notes: "" };

export default function SellLand() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.location.trim()) {
      toast.error("Please complete your name, email and land location.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/land-submissions`, form);
      setDone(true);
      setForm(initial);
      toast.success("Thank you — your submission has been received.");
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="sell-land-page" className="bg-[#0B0F0E]">
      <Toaster position="top-center" richColors theme="dark" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12">
        <div className="overline"><span className="h-line" />Sell Your Land</div>
        <h1 className="font-display text-[#F4EFE6] text-6xl md:text-7xl lg:text-[5.5rem] mt-7 leading-[0.95] max-w-4xl">
          Tell us about your site — <span className="italic-gold">confidentially.</span>
        </h1>
        <p className="text-[#9BA39B] text-lg max-w-3xl mt-9 leading-relaxed">
          Share a few details — greenfield, brownfield, residential or commercial — and our team will respond with a no-obligation appraisal, typically within five working days. We never share landowner information.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="bg-[#11171A] border border-[#F4EFE6]/10 p-8 md:p-12">
            {done ? (
              <div data-testid="sell-land-success" className="py-12 text-center">
                <CheckCircle2 className="text-[#C9A24B] mx-auto" size={48} />
                <h2 className="font-display text-4xl text-[#F4EFE6] mt-6">Submission received.</h2>
                <p className="text-[#9BA39B] mt-4 max-w-md mx-auto">Thank you. A member of the Upsurge team will be in touch shortly using the contact details you provided.</p>
                <button data-testid="sell-land-reset" onClick={() => setDone(false)} className="usl-btn-outline mt-8">Submit Another Site</button>
              </div>
            ) : (
              <form data-testid="sell-land-form" onSubmit={submit} className="space-y-7">
                <div className="grid md:grid-cols-2 gap-7">
                  <div>
                    <label className="overline block mb-2">Your name *</label>
                    <input data-testid="sell-land-name" className="underline-input" value={form.name} onChange={onChange("name")} placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Email *</label>
                    <input data-testid="sell-land-email" type="email" className="underline-input" value={form.email} onChange={onChange("email")} placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Telephone</label>
                    <input data-testid="sell-land-phone" className="underline-input" value={form.phone} onChange={onChange("phone")} placeholder="07000 000 000" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Approx. acreage</label>
                    <input data-testid="sell-land-acreage" className="underline-input" value={form.acreage} onChange={onChange("acreage")} placeholder="e.g. 25 acres" />
                  </div>
                </div>

                <div>
                  <label className="overline block mb-2">Site location *</label>
                  <input data-testid="sell-land-location" className="underline-input" value={form.location} onChange={onChange("location")} placeholder="Village / town and county" />
                </div>

                <div>
                  <label className="overline block mb-2">Current planning status</label>
                  <select data-testid="sell-land-status" className="underline-input" value={form.planning_status} onChange={onChange("planning_status")}>
                    <option>Unknown</option>
                    <option>Greenfield — no planning history</option>
                    <option>Brownfield / previously developed</option>
                    <option>Promoted by another party</option>
                    <option>SHLAA / call-for-sites submitted</option>
                    <option>Allocated in Local Plan</option>
                    <option>Outline consent</option>
                    <option>Full / Reserved-matters consent</option>
                  </select>
                </div>

                <div>
                  <label className="overline block mb-2">Tell us more</label>
                  <textarea data-testid="sell-land-notes" rows={5} className="underline-input resize-none" value={form.notes} onChange={onChange("notes")} placeholder="Any planning context, ambitions or timing considerations..." />
                </div>

                <div className="pt-2">
                  <button data-testid="sell-land-submit" type="submit" disabled={loading} className="usl-btn">
                    {loading ? "Sending..." : "Submit confidentially"} <ArrowUpRight size={16} />
                  </button>
                  <p className="text-xs text-[#9BA39B] mt-4">By submitting you agree we may contact you about your enquiry. We never share your details.</p>
                </div>
              </form>
            )}
          </div>
        </div>

        <aside className="lg:col-span-5 lg:pl-4">
          <div className="bg-[#C9A24B] text-[#0B0F0E] p-8 md:p-10">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold">Why landowners choose Upsurge</div>
            <ul className="mt-6 space-y-5 text-[15px] leading-relaxed">
              {[
                "We carry all promotion costs — surveys, planning, remediation, legal.",
                "Transparent agreements aligned with your interests.",
                "Patient capital — happy to work over multiple Local Plans.",
                "Specialist brownfield & viability expertise.",
                "Nationwide reach with local authority insight.",
              ].map((t) => (
                <li key={t} data-testid={`why-${t.slice(0, 20)}`} className="flex gap-3"><CheckCircle2 size={18} className="text-[#0B0F0E] mt-0.5 shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
          </div>

          <div className="mt-8 border border-[#F4EFE6]/10 p-8 bg-[#11171A]">
            <div className="overline">Prefer to speak?</div>
            <div className="font-display text-3xl text-[#F4EFE6] mt-3">{SITE.phone}</div>
            <a data-testid="sell-land-email-link" href={`mailto:${SITE.email}`} className="text-[#C9A24B] underline-offset-4 hover:underline mt-3 inline-block">{SITE.email}</a>
          </div>
        </aside>
      </section>
    </div>
  );
}
